import type {
  GlobalRole,
  ProjectRole,
  Permission,
  Resource,
  PermissionContext,
  PermissionCheckResult,
  PermissionUser,
  PermissionApp,
} from '../types/permissions';

/**
 * Тип владения приложением
 */
export type AppOwnership = 'own' | 'shared';

/**
 * Результат проверки с контекстом владения
 */
export interface EnhancedPermissionCheckResult extends PermissionCheckResult {
  ownership?: AppOwnership;
  effectiveRole?: GlobalRole;
}

/**
 * Матрица прав доступа
 * Определяет какие действия доступны для каждой комбинации глобальной и проектной ролей
 * 
 * ПРАВИЛА:
 * 1. analyst: полный доступ (создание, редактирование, удаление, смена статуса, управление участниками)
 * 2. developer: только просмотр и смена статуса
 * 3. viewer: только просмотр
 * 
 * ДЛЯ СВОИХ ПРОЕКТОВ (ownership === 'own'): используется глобальная роль (globalRole)
 * ДЛЯ ЧУЖИХ ПРОЕКТОВ (ownership === 'shared'): используется проектная роль (projectRole)
 */
const PERMISSION_MATRIX: Record<GlobalRole, Record<ProjectRole | 'none', Permission[]>> = {
  analyst: {
    analyst: ['view', 'create', 'edit', 'delete', 'manage_members', 'change_status'], // Analyst: полный доступ
    developer: ['view', 'change_status'], // Analyst может только просматривать и менять статус в проектах developer
    viewer: ['view'], // Analyst может только просматривать проекты viewer
    none: ['view', 'create'], // Analyst может создавать новые проекты
  },
  developer: {
    analyst: ['view', 'change_status'], // Developer может только просматривать и менять статус в проектах analyst
    developer: ['view', 'change_status'], // Developer может только просматривать и менять статус в проектах developer
    viewer: ['view'], // Developer может только просматривать проекты viewer
    none: ['view'], // Developer может просматривать доступные проекты
  },
  viewer: {
    analyst: ['view'], // Viewer может только просматривать проекты analyst
    developer: ['view'], // Viewer может только просматривать проекты developer
    viewer: ['view'], // Viewer может только просматривать проекты viewer
    none: ['view'], // Viewer только просматривает
  },
  pending: {
    analyst: [],
    developer: [],
    viewer: [],
    none: [],
  },
};

/**
 * Права для владельца приложения (применяются независимо от глобальной роли)
 * ЗАРЕЗЕРВИРОВАНО: сейчас используется getEffectiveRole для определения прав
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const OWNER_PERMISSIONS: Permission[] = [
  'view', 'create', 'edit', 'delete', 'manage_members', 'change_status'
];

/**
 * Описания действий для сообщений об ошибках
 */
const PERMISSION_DESCRIPTIONS: Record<Permission, string> = {
  view: 'перегляд',
  create: 'створення',
  edit: 'редагування',
  delete: 'видалення',
  manage_members: 'управління учасниками',
  change_status: 'зміну статусу',
};

/**
 * Сервис для проверки прав доступа
 */
export const permissionService = {
  /**
   * Определяет тип владения приложением
   */
  getAppOwnership(user: PermissionUser | null | undefined, app: PermissionApp): AppOwnership {
    if (!user) return 'shared';
    return app.ownerId === user.id ? 'own' : 'shared';
  },

  /**
   * Получает эффективную роль пользователя для приложения
   * 
   * ПРАВИЛА:
   * 1. Для своих проектов (ownership === 'own'): используем глобальную роль из профиля
   * 2. Для чужих проектов (ownership === 'shared'): используем роль из app.members[userId].role
   * 
   * ВАЖНО: Owner проекта теперь имеет права СВОЕЙ роли из профиля, а не полный доступ!
   */
  getEffectiveRole(
    user: PermissionUser | null | undefined,
    app: PermissionApp
  ): { role: GlobalRole; ownership: AppOwnership } {
    if (!user) {
      return { role: 'pending', ownership: 'shared' };
    }

    const ownership = this.getAppOwnership(user, app);

    // Для своих проектов - используем глобальную роль
    if (ownership === 'own') {
      return { role: user.role, ownership };
    }

    // Для чужих проектов - используем роль из members
    const memberRole = app.members?.[user.id]?.role as GlobalRole | undefined;
    
    // Если пользователь участник проекта - возвращаем его роль в проекте
    if (memberRole) {
      return { role: memberRole, ownership };
    }

    // Если нет роли в проекте - возвращаем глобальную роль
    return { role: user.role, ownership };
  },

  /**
   * Проверяет наличие права на действие
   * 
   * УЧИТЫВАЕТ:
   * 1. Глобальную роль пользователя (analyst/developer/viewer)
   * 2. Проектную роль (developer/viewer) - только для shared проектов
   */
  hasPermission(
    context: PermissionContext,
    permission: Permission,
    resource?: Resource
  ): EnhancedPermissionCheckResult {
    const targetResource = resource || context.resource;

    // Pending роль не имеет никаких прав
    if (context.globalRole === 'pending') {
      return {
        allowed: false,
        reason: 'Необхідно обрати роль користувача',
        requiredRole: 'analyst, developer або viewer',
      };
    }

    // Используем только глобальную роль для проверки прав
    // Владение (own/shared) не дает дополнительных прав
    const effectiveProjectRole = context.projectRole || 'none';

    // Получаем разрешенные действия для комбинации ролей
    const allowedPermissions =
      PERMISSION_MATRIX[context.globalRole]?.[effectiveProjectRole] || [];

    // Проверяем наличие права
    const allowed = allowedPermissions.includes(permission);

    if (allowed) {
      return { 
        allowed: true,
        ownership: context.isOwner ? 'own' : 'shared',
        effectiveRole: context.globalRole
      };
    }

    return {
      allowed: false,
      reason: `У вас немає прав для ${PERMISSION_DESCRIPTIONS[permission]} ${targetResource}`,
      requiredRole: this.getRequiredRole(permission),
      ownership: context.isOwner ? 'own' : 'shared',
      effectiveRole: context.globalRole
    };
  },

  /**
   * Проверяет можно ли редактировать приложение
   * 
   * ПРАВИЛА:
   * - own проекты: analyst ✅ полный доступ
   * - shared проекты: только analyst, НО нельзя менять name/description (только events/versions)
   * 
   * ВАЖНО: Для своих проектов используется глобальная роль из профиля!
   */
  canEditApp(user: PermissionUser | null | undefined, app: PermissionApp): boolean {
    if (!user) return false;
    if (user.role === 'pending') return false;

    const { role, ownership } = this.getEffectiveRole(user, app);

    // Debug logging
    console.log('[permissionService.canEditApp] Debug:', {
      userId: user.id,
      userRole: user.role,
      effectiveRole: role,
      ownership,
      appOwnerId: app.ownerId,
      isOwner: app.ownerId === user.id,
      result: ownership === 'shared' ? role === 'analyst' : role === 'analyst'
    });

    // Для shared проектов - только analyst
    if (ownership === 'shared') {
      return role === 'analyst';
    }

    // Для своих проектов - только analyst может редактировать
    return role === 'analyst';
  },

  /**
   * Проверяет можно ли редактировать название/описание приложения
   * 
   * ПРАВИЛА:
   * - own проекты: analyst ✅ полный доступ
   * - shared проекты: ❌ НЕЛЬЗЯ менять name/description (только events/versions)
   * 
   * Используется в UI для скрытия/показа полей name/description при редактировании
   */
  canEditAppDetails(user: PermissionUser | null | undefined, app: PermissionApp): boolean {
    if (!user) return false;
    if (user.role === 'pending') return false;

    const { role, ownership } = this.getEffectiveRole(user, app);

    // Для shared проектов - изменение name/description запрещено
    if (ownership === 'shared') {
      return false;
    }

    // Только analyst может редактировать name/description в своих проектах
    return role === 'analyst';
  },

  /**
   * Проверяет можно ли создавать ресурсы в приложении
   * 
   * ПРАВИЛА:
   * - analyst: ✅ полный доступ (create, edit, delete, manage_members, change_status)
   * - developer: ❌ не может создавать (только view + change_status)
   * - viewer: ❌
   * 
   * ВАЖНО: Для своих проектов используется глобальная роль из профиля!
   */
  canCreateInApp(user: PermissionUser | null | undefined, app: PermissionApp): boolean {
    if (!user) return false;
    if (user.role === 'pending') return false;

    const { role } = this.getEffectiveRole(user, app);

    // Только analyst может создавать
    return role === 'analyst';
  },

  /**
   * Проверяет можно ли удалить приложение
   * 
   * ПРАВИЛА:
   * - own проекты: analyst ✅ может удалять
   * - shared проекты: ❌ НЕЛЬЗЯ удалять (только owner может удалить свой проект)
   * 
   * ВАЖНО: Owner может удалить только если его глобальная роль analyst!
   */
  canDeleteApp(user: PermissionUser | null | undefined, app: PermissionApp): boolean {
    if (!user) return false;
    if (user.role === 'pending') return false;

    const { role, ownership } = this.getEffectiveRole(user, app);

    // Для shared проектов - удаление запрещено
    if (ownership === 'shared') {
      return false;
    }

    // Только analyst может удалять свои проекты
    return role === 'analyst';
  },

  /**
   * Проверяет можно ли управлять участниками
   * 
   * ПРАВИЛА:
   * - own проекты: analyst ✅ полный доступ
   * - shared проекты: ❌ НЕЛЬЗЯ управлять участниками (приглашать/удалять)
   * 
   * ВАЖНО: Для своих проектов используется глобальная роль из профиля!
   */
  canManageMembers(user: PermissionUser | null | undefined, app: PermissionApp): boolean {
    if (!user) return false;

    const { role, ownership } = this.getEffectiveRole(user, app);

    // Для shared проектов - управление участниками запрещено
    if (ownership === 'shared') {
      return false;
    }

    // Только analyst может управлять участниками в своих проектах
    return role === 'analyst';
  },

  /**
   * Проверяет можно ли изменить статус события
   * 
   * ПРАВИЛА:
   * - analyst: ✅ любой статус
   * - developer: ✅ только между "Впроваджено" и предыдущим статусом
   * - viewer: ❌
   */
  canChangeStatus(user: PermissionUser | null | undefined, app: PermissionApp): boolean {
    if (!user) return false;
    if (user.role === 'pending') return false;

    const { role } = this.getEffectiveRole(user, app);

    // Analyst может менять любой статус
    if (role === 'analyst') return true;

    // Developer может менять только статус
    if (role === 'developer') return true;

    // Viewer не может менять статус
    return false;
  },

  /**
   * Проверяет, может ли пользователь изменить статус события
   * с учетом ограничений для developer
   *
   * ПРАВИЛА ДЛЯ DEVELOPER:
   * - Может: new ↔ implemented
   * - Может: edited ↔ implemented
   * - Может: pending_deletion ↔ deleted
   * - НЕ может менять статус, если последним его менял analyst (для implemented/deleted)
   */
  canChangeEventStatus(
    user: PermissionUser | null | undefined,
    app: PermissionApp,
    currentStatus?: string,
    newStatus?: string,
    lastStatusChangedBy?: string // 'analyst' | 'developer' | 'viewer'
  ): boolean {
    if (!user) return false;
    if (user.role === 'pending') return false;

    const { role } = this.getEffectiveRole(user, app);

    // Analyst может менять любой статус
    if (role === 'analyst') return true;

    // Developer имеет ограниченные права
    if (role === 'developer') {
      // Если не указаны статусы - разрешаем проверку (для UI)
      if (!currentStatus || !newStatus) return true;

      // Если последним статус implemented или deleted установил analyst - developer не может менять
      if ((currentStatus === 'implemented' || currentStatus === 'deleted') &&
          lastStatusChangedBy === 'analyst') {
        return false;
      }

      // Developer может:
      // 1. new ↔ implemented (в обе стороны)
      // 2. edited ↔ implemented (в обе стороны)
      // 3. pending_deletion ↔ deleted (в обе стороны)
      const allowedTransitions = [
        // new ↔ implemented
        { from: 'new', to: 'implemented' },
        { from: 'implemented', to: 'new' },
        // edited ↔ implemented
        { from: 'edited', to: 'implemented' },
        { from: 'implemented', to: 'edited' },
        // pending_deletion ↔ deleted
        { from: 'pending_deletion', to: 'deleted' },
        { from: 'deleted', to: 'pending_deletion' },
      ];

      return allowedTransitions.some(
        t => t.from === currentStatus && t.to === newStatus
      );
    }

    // Viewer не может менять статус
    return false;
  },

  /**
   * Проверяет можно ли создать ресурс
   * 
   * ПРАВИЛА:
   * - app: только analyst
   * - screen/event/version: analyst в своих или с правами admin/editor в чужих
   */
  canCreate(
    user: PermissionUser | null | undefined,
    resource: Resource,
    app?: PermissionApp
  ): boolean {
    if (!user) return false;
    if (user.role === 'pending') return false;

    // Для создания проекта (app) нужна роль analyst
    if (resource === 'app') {
      return user.role === 'analyst';
    }

    // Для остальных ресурсов нужен доступ к проекту
    if (!app) return false;

    return this.canCreateInApp(user, app);
  },

  /**
   * Проверяет можно ли добавить комментарий к событию
   * 
   * ПРАВИЛА:
   * - analyst: ✅ полный доступ
   * - developer: ✅ может добавлять
   * - viewer: ✅ может добавлять комментарии (это не редактирование события)
   * 
   * ВАЖНО: Viewer может создавать комментарии, но не может редактировать/удалять события
   */
  canAddComment(user: PermissionUser | null | undefined, _app: PermissionApp): boolean {
    if (!user) return false;
    if (user.role === 'pending') return false;

    // Все роли могут добавлять комментарии (analyst, developer, viewer)
    // Комментарий - это не редактирование самого события
    return true;
  },

  /**
   * Проверяет можно ли просматривать ресурс
   */
  canView(
    user: PermissionUser | null | undefined,
    resource: Resource,
    app?: PermissionApp
  ): boolean {
    if (!user) return false;
    if (user.role === 'pending') return false;

    // Viewer всегда может просматривать
    if (user.role === 'viewer') return true;

    if (!app) {
      // Для apps списка - analyst и developer могут просматривать
      return ['analyst', 'developer'].includes(user.role);
    }

    const memberRole = app.members?.[user.id]?.role as ProjectRole | undefined;

    const context: PermissionContext = {
      globalRole: user.role,
      projectRole: memberRole,
      isOwner: app.ownerId === user.id,
      resource,
    };

    return this.hasPermission(context, 'view').allowed;
  },

  /**
   * Проверяет можно ли редактировать событие
   * 
   * ПРАВИЛА:
   * - analyst: ✅
   * - developer: ❌
   * - viewer: ❌
   * 
   * ВАЖНО: Owner редактирует по СВОЕЙ роли из профиля!
   */
  canEditEvent(user: PermissionUser | null | undefined, app: PermissionApp): boolean {
    if (!user) return false;
    
    const { role } = this.getEffectiveRole(user, app);
    
    return role === 'analyst';
  },

  /**
   * Проверяет можно ли удалить событие
   * 
   * ПРАВИЛА:
   * - analyst: ✅
   * - developer: ❌
   * - viewer: ❌
   * 
   * ВАЖНО: Owner удаляет по СВОЕЙ роли из профиля!
   */
  canDeleteEvent(user: PermissionUser | null | undefined, app: PermissionApp): boolean {
    if (!user) return false;
    
    const { role } = this.getEffectiveRole(user, app);
    
    return role === 'analyst';
  },

  /**
   * Проверяет можно ли удалить версию
   * 
   * ПРАВИЛА:
   * - analyst: ✅ (в своих и в чужих проектах)
   * - developer: ❌
   * - viewer: ❌
   * 
   * ВАЖНО: Owner удаляет по СВОЕЙ роли из профиля!
   */
  canDeleteVersion(user: PermissionUser | null | undefined, app: PermissionApp): boolean {
    if (!user) return false;
    if (user.role === 'pending') return false;
    
    const { role, ownership } = this.getEffectiveRole(user, app);
    
    // Debug logging
    console.log('[permissionService.canDeleteVersion] Debug:', {
      userId: user.id,
      userRole: user.role,
      effectiveRole: role,
      ownership,
      appOwnerId: app.ownerId,
      isOwner: app.ownerId === user.id
    });
    
    return role === 'analyst';
  },

  /**
   * Проверяет имеет ли пользователь read-only доступ
   */
  isReadOnly(user: PermissionUser | null | undefined, app: PermissionApp): boolean {
    if (!user) return true;

    const { role } = this.getEffectiveRole(user, app);
    return role === 'viewer';
  },

  /**
   * Проверяет является ли пользователь analyst для данного приложения
   */
  isAnalyst(user: PermissionUser | null | undefined, app: PermissionApp): boolean {
    if (!user) return false;
    const { role } = this.getEffectiveRole(user, app);
    return role === 'analyst';
  },

  /**
   * Проверяет является ли пользователь developer для данного приложения
   */
  isDeveloper(user: PermissionUser | null | undefined, app: PermissionApp): boolean {
    if (!user) return false;
    const { role } = this.getEffectiveRole(user, app);
    return role === 'developer';
  },

  /**
   * Проверяет, может ли пользователь выполнить хотя бы одно действие из списка
   */
  hasAnyPermission(
    user: PermissionUser | null | undefined,
    permissions: Permission[],
    app?: PermissionApp,
    resource: Resource = 'app'
  ): boolean {
    if (!user) return false;

    const effectiveApp = app || { id: '', ownerId: '', members: {} };
    const { role, ownership } = this.getEffectiveRole(user, effectiveApp);

    const memberRole = app ? (app.members?.[user.id]?.role as ProjectRole | undefined) : undefined;

    const context: PermissionContext = {
      globalRole: role, // Используем эффективную роль
      projectRole: memberRole,
      isOwner: ownership === 'own',
      resource,
    };

    return permissions.some((permission) => this.hasPermission(context, permission).allowed);
  },

  /**
   * Проверяет, может ли пользователь выполнить все действия из списка
   */
  hasAllPermissions(
    user: PermissionUser | null | undefined,
    permissions: Permission[],
    app?: PermissionApp,
    resource: Resource = 'app'
  ): boolean {
    if (!user) return false;

    const effectiveApp = app || { id: '', ownerId: '', members: {} };
    const { role, ownership } = this.getEffectiveRole(user, effectiveApp);

    const memberRole = app ? (app.members?.[user.id]?.role as ProjectRole | undefined) : undefined;

    const context: PermissionContext = {
      globalRole: role, // Используем эффективную роль
      projectRole: memberRole,
      isOwner: ownership === 'own',
      resource,
    };

    return permissions.every((permission) => this.hasPermission(context, permission).allowed);
  },

  /**
   * Получает требуемую роль для действия
   */
  getRequiredRole(permission: Permission): string {
    // Находим минимальную роль, которая имеет это право
    for (const [globalRole, projectRoles] of Object.entries(PERMISSION_MATRIX)) {
      if (globalRole === 'pending') continue;

      for (const [projectRole, permissions] of Object.entries(projectRoles)) {
        if (permissions.includes(permission)) {
          if (projectRole === 'developer') {
            return 'розробник проекту';
          }
          if (projectRole === 'viewer') {
            return 'глядач проекту';
          }
          return globalRole === 'analyst' ? 'аналітик' : 
                 globalRole === 'developer' ? 'розробник' : 
                 globalRole === 'viewer' ? 'глядач' : globalRole;
        }
      }
    }
    return 'невідома роль';
  },

  /**
   * Получает список всех разрешенных действий для контекста
   */
  getAllowedPermissions(context: PermissionContext): Permission[] {
    if (context.globalRole === 'pending') {
      return [];
    }

    // Используем только глобальную роль, владение не дает дополнительных прав
    const effectiveProjectRole = context.projectRole || 'none';
    return PERMISSION_MATRIX[context.globalRole]?.[effectiveProjectRole] || [];
  },

  /**
   * Получает описание прав пользователя для приложения
   */
  getPermissionsDescription(
    user: PermissionUser | null | undefined,
    app: PermissionApp
  ): { role: string; canEdit: boolean; canCreate: boolean; canDelete: boolean; isReadOnly: boolean } {
    if (!user) {
      return { role: 'не авторизован', canEdit: false, canCreate: false, canDelete: false, isReadOnly: true };
    }

    const { role, ownership } = this.getEffectiveRole(user, app);
    const isReadOnly = role === 'viewer';
    const canEdit = this.canEditApp(user, app);
    const canCreate = this.canCreateInApp(user, app);
    const canDelete = this.canDeleteApp(user, app);

    let roleLabel: string = role;
    if (ownership === 'own') {
      roleLabel = `${role} (власник)` as const;
    }

    return {
      role: roleLabel,
      canEdit,
      canCreate,
      canDelete,
      isReadOnly
    };
  }
};

/**
 * Класс ошибки доступа
 */
export class PermissionError extends Error {
  constructor(
    message: string,
    public permission: Permission,
    public code: string = 'permission-denied'
  ) {
    super(message);
    this.name = 'PermissionError';
  }
}

export default permissionService;